import React, { useState, useRef, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useKpiMonthlyQuery, useKpiComparisonQuery } from '../../hooks/queries/useDashboardQuery';
import './KpiCard.css';

interface KpiMetrics {
  gross_sales: string | number;
  total_refunds: string | number;
  net_sales: string | number;
  order_count: string | number;
  avg_ticket: string | number;
  avg_processing_time: string | number;
}

interface MonthlyTrendData {
  year_month: string;
  gross_sales: string | number;
  total_refunds: string | number;
  net_sales: string | number;
  order_count: string | number;
  avg_ticket: string | number;
  avg_processing_time: string | number;
  dine_in_gross_sales: string | number;
  dine_in_total_refunds: string | number;
  dine_in_net_sales: string | number;
  dine_in_order_count: string | number;
  dine_in_avg_ticket: string | number;
  dine_in_avg_processing_time: string | number;
  non_dine_in_gross_sales: string | number;
  non_dine_in_total_refunds: string | number;
  non_dine_in_net_sales: string | number;
  non_dine_in_order_count: string | number;
  non_dine_in_avg_ticket: string | number;
  non_dine_in_avg_processing_time: string | number;
}

interface KpiCardProps {
  title: string;
  icon?: string;
  metricKey: 'net_sales' | 'order_count' | 'avg_ticket' | 'avg_processing_time';
  totalData?: KpiMetrics;
  dineInData?: KpiMetrics;
  nonDineInData?: KpiMetrics;
  formatFn?: (value: any) => string;
  isLoading?: boolean;
  color?: string;
  accentColor?: string;
  showPercentage?: boolean;
}

interface ModalPosition {
  y: number;
}

const getSalesforceIdFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId') || 'a0JPC00000cKHlB2AW';
};

const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const calculateYAxisRange = (values: number[], metricKey: string) => {
  const validValues = values.filter(v => v > 0 && isFinite(v));
  
  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }
  
  const minValue = Math.min(...validValues);
  const maxValue = Math.max(...validValues);
  const range = maxValue - minValue;
  
  switch (metricKey) {
    case 'avg_ticket':
      const ticketMargin = Math.max(range * 0.1, 2);
      return {
        min: Math.max(0, Math.floor(minValue - ticketMargin)),
        max: Math.ceil(maxValue + range * 0.05)
      };
      
    case 'avg_processing_time':
      if (minValue > range * 0.3) {
        return {
          min: Math.max(0, Math.floor(minValue - range * 0.1)),
          max: Math.ceil(maxValue + range * 0.1)
        };
      }
      return {
        min: 0,
        max: Math.ceil(maxValue + range * 0.1)
      };
      
    case 'net_sales':
    case 'order_count':
      return {
        min: Math.max(0, Math.floor(minValue - range * 0.05)),
        max: Math.ceil(maxValue + range * 0.05)
      };
      
    default:
      return {
        min: Math.max(0, Math.floor(minValue - range * 0.1)),
        max: Math.ceil(maxValue + range * 0.1)
      };
  }
};

const KpiCard: React.FC<KpiCardProps> = ({ 
  title, 
  icon, 
  metricKey, 
  totalData, 
  dineInData, 
  nonDineInData,
  formatFn = (value) => String(value),
  isLoading = false,
  color = '#474646',
  accentColor = '#F97F76',
  showPercentage = false
}) => {
  const { t } = useTranslation();
  const { 
    isFiltersApplying,
    dateRange,
    dayOfWeek,
    time,
    diningBehavior
  } = useFilters();
  
  const { isLaptop, isiPad, isiPhone } = useResponsive();

  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState<'monthly' | 'comparison'>('monthly');
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ y: 0 });
  const chartRef = useRef<any>(null);
  
  
  const getSalesforceIdFromUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('storeId') || '';
  };
  
  const salesforceId = getSalesforceIdFromUrl();
  
  
  const {
    data: monthlyData = [],
    isLoading: monthlyLoading,
    error: monthlyError
  } = useKpiMonthlyQuery({
    storeId: salesforceId,
    filters: { dayOfWeek, time, diningBehavior },
    enabled: showModal && viewMode === 'monthly'
  });
  
  const {
    data: comparisonData = {},
    isLoading: comparisonLoading,
    error: comparisonError
  } = useKpiComparisonQuery({
    storeId: salesforceId,
    filters: { dayOfWeek, time, diningBehavior },
    enabled: showModal && viewMode === 'comparison'
  });
  
  const trendLoading = monthlyLoading || comparisonLoading;
  const trendError = monthlyError || comparisonError;
  const trendErrorMessage = trendError instanceof Error ? trendError.message : trendError; 

  const calculateModalPosition = (event: React.MouseEvent): ModalPosition => {
    const clickY = event.clientY;
    const modalHeight = 600;
    const padding = 20;
    const screenHeight = window.innerHeight;
    
    let finalY = clickY;
    
    if (clickY + modalHeight + padding > screenHeight) {
      finalY = screenHeight - modalHeight - padding;
    }
    
    if (finalY < padding) {
      finalY = padding;
    }
    
    return { y: finalY };
  };

  const calculateModalPositionBelowButton = (event: React.MouseEvent): ModalPosition => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const finalY = rect.bottom + 10;
    return { y: finalY };
  };

  const handleTrendButtonClick = (event: React.MouseEvent) => {
    const position = calculateModalPosition(event);
    setModalPosition(position);
    setViewMode('monthly');
    setShowModal(true);
  };

  const handleComparisonButtonClick = (event: React.MouseEvent) => {
    const position = calculateModalPosition(event);
    setModalPosition(position);
    setViewMode('comparison');
    setShowModal(true);
  };


  const updateLineStyles = (selected: {[key: string]: boolean}) => {
    if (!chartRef.current) return;
    
    const echartInstance = chartRef.current.getEchartsInstance();
    const currentOption = echartInstance.getOption();
    
    const dineInActive = selected[t('kpi.dineIn')];
    const nonDineInActive = selected[t('kpi.takeout')];
    
    const newSeries = currentOption.series.map((series: any, index: number) => {
      if (series.name === t('kpi.dineIn')) {
        return {
          ...series,
          lineStyle: {
            ...series.lineStyle,
            width: isLaptop ? 2 : 2,
            type: (dineInActive && nonDineInActive) ? 'dashed' : 'solid'
          }
        };
      } else if (series.name === t('kpi.takeout')) {
        return {
          ...series,
          lineStyle: {
            ...series.lineStyle,
            width: (dineInActive && nonDineInActive) ? (isLaptop ? 4 : 4) : (isLaptop ? 2 : 2)
          }
        };
      }
      return series;
    });
    
    echartInstance.setOption({
      series: newSeries
    });
  };

  const getChartEvents = () => {
    return {
      'legendselectchanged': function(params: any) {
        updateLineStyles(params.selected);
      }
    };
  };

  const closeModal = () => {
    setShowModal(false);
    setViewMode('monthly');
  };

  const getTrendOption = () => {
    if (viewMode === 'comparison') {
      return getComparisonOption();
    }
    
    if (monthlyData.length === 0) {
      return {};
    }

    const months = monthlyData.map((item: MonthlyTrendData) => formatMonthForDisplay(item.year_month));
    
    const extractNumericValue = (value: string | number): number => {
      if (typeof value === 'string') {
        return parseFloat(value.replace(/[$,%]/g, ''));
      }
      return Number(value) || 0;
    };
    
    const getDaysInMonth = (yearMonth: string): number => {
      const [year, month] = yearMonth.split('-');
      return new Date(parseInt(year), parseInt(month), 0).getDate();
    };
    
    let totalValues: number[] = [];
    let dineInValues: number[] = [];
    let nonDineInValues: number[] = [];
    let yAxisLabel = '';
    let tooltipFormatter: (value: number) => string = (value) => value.toString();

    switch (metricKey) {
      case 'net_sales':
        totalValues = monthlyData.map((item: MonthlyTrendData) => {
          const monthlyTotal = extractNumericValue(item.net_sales);
          const daysInMonth = getDaysInMonth(item.year_month);
          return monthlyTotal / daysInMonth;
        });
        dineInValues = monthlyData.map((item: MonthlyTrendData) => {
          const monthlyTotal = extractNumericValue(item.dine_in_net_sales);
          const daysInMonth = getDaysInMonth(item.year_month);
          return monthlyTotal / daysInMonth;
        });
        nonDineInValues = monthlyData.map((item: MonthlyTrendData) => {
          const monthlyTotal = extractNumericValue(item.non_dine_in_net_sales);
          const daysInMonth = getDaysInMonth(item.year_month);
          return monthlyTotal / daysInMonth;
        });
        yAxisLabel = t('kpi.yAxisDailyAvgSales');
        tooltipFormatter = (value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        break;
        
      case 'order_count':
        dineInValues = monthlyData.map((item: MonthlyTrendData) => {
          const monthlyTotal = extractNumericValue(item.dine_in_order_count);
          const daysInMonth = getDaysInMonth(item.year_month);
          return monthlyTotal / daysInMonth;
        });
        nonDineInValues = monthlyData.map((item: MonthlyTrendData) => {
          const monthlyTotal = extractNumericValue(item.non_dine_in_order_count);
          const daysInMonth = getDaysInMonth(item.year_month);
          return monthlyTotal / daysInMonth;
        });
        yAxisLabel = t('kpi.yAxisDailyOrders');
        tooltipFormatter = (value) => Math.round(value).toLocaleString();
        break;
        
      case 'avg_ticket':
        totalValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.avg_ticket));
        dineInValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.dine_in_avg_ticket));
        nonDineInValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.non_dine_in_avg_ticket));
        yAxisLabel = t('kpi.yAxisAvgTicket');
        tooltipFormatter = (value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        break;
        
      case 'avg_processing_time':
        totalValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.avg_processing_time || 0));
        dineInValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.dine_in_avg_processing_time || 0));
        nonDineInValues = monthlyData.map((item: MonthlyTrendData) => extractNumericValue(item.non_dine_in_avg_processing_time || 0));
        yAxisLabel = t('kpi.yAxisAvgTime');
        tooltipFormatter = (value) => `${Math.round(value)}${t('kpi.secondsSuffix')}`;
        break;
    }

    const allValues = metricKey === 'order_count' 
      ? [...dineInValues, ...nonDineInValues, ...dineInValues.map((dineIn, index) => dineIn + nonDineInValues[index])]
      : [...totalValues, ...dineInValues, ...nonDineInValues];
    const yAxisRange = calculateYAxisRange(allValues, metricKey);

    if (metricKey === 'order_count' || metricKey === 'net_sales') {
      return {
        grid: {
          left: '4%',
          right: '4%',
          top: '14%',
          bottom: '8%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#ccc',
          borderWidth: 1,
          textStyle: {
            color: '#333',
            fontSize: isLaptop ? 11 : 11
          },
          formatter: function(params: any) {
            const monthStr = params[0].axisValue;
            let html = `<div style="padding: 3px;">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? 6 : 6}px; font-size: ${isLaptop ? 12 : 12}px;">${monthStr}</div>`;
            
            const dineInValue = params.find((p: any) => p.seriesName === t('kpi.dineIn'))?.value || 0;
            const nonDineInValue = params.find((p: any) => p.seriesName === t('kpi.takeout'))?.value || 0;
            const totalValue = dineInValue + nonDineInValue;
            
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? 6 : 6}px; padding-bottom: 3px; border-bottom: 1px solid #eee;">
              <span style="font-weight: bold; color: #474646;">${t('kpi.tooltipTotal')}</span>
              <span style="font-weight: bold; font-size: ${isLaptop ? 12 : 12}px; color: #474646;">${tooltipFormatter(totalValue)}</span>
            </div>`;
            
            params.forEach((param: any) => {
              const percentage = totalValue > 0 ? ((param.value / totalValue) * 100).toFixed(1) : '0.0';
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: ${isLaptop ? 8 : 8}px; height: ${isLaptop ? 8 : 8}px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                  <strong>${param.seriesName}:</strong>
                </span>
                <span style="font-weight: bold; font-size: ${isLaptop ? 11 : 11}px;">
                  ${tooltipFormatter(param.value)} 
                  <span style="color: #999; font-size: ${isLaptop ? 10 : 10}px;">(${percentage}%)</span>
                </span>
              </div>`;
            });
            
            html += '</div>';
            return html;
          }
        },
        legend: {
          data: [t('kpi.dineIn'), t('kpi.takeout')],
          top: '6%',
          textStyle: {
            fontSize: isiPhone ? 9 : (isLaptop ? 11 : 11),
            fontWeight: 'bold'
          },
          itemGap: isLaptop ? 15 : 15,
          selector: false,
          itemStyle: {
            borderWidth: 0
          },
          emphasis: {
            itemStyle: {
              shadowBlur: isLaptop ? 10 : 10,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          }
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '1%',
            style: {
              text: t('kpi.chartHintStacked'),
              fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
              fontWeight: 'normal',
              fill: '#666',
              fontStyle: 'italic'
            }
          }
        ],
        xAxis: {
          type: 'category',
          data: months,
          axisLabel: {
            fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
            fontWeight: 'bold',
            rotate: months.length > 8 ? 45 : 0,
            margin: isLaptop ? 6 : 6
          },
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: yAxisLabel,
          min: yAxisRange.min,
          scale: true,
          nameTextStyle: {
            fontSize: isiPhone ? 9 : (isLaptop ? 11 : 11),
            fontWeight: 'bold'
          },
          axisLabel: {
            fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
            formatter: (value: number) => Math.round(value).toString()
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        series: [
          {
            name: t('kpi.dineIn'),
            type: 'line',
            stack: 'total',
            data: dineInValues,
            lineStyle: {
              width: isLaptop ? 2 : 2,
              color: '#474646',
              type: 'dashed'
            },
            itemStyle: {
              color: '#474646'
            },
            areaStyle: {
              color: 'rgba(71, 70, 70, 0.6)'
            },
            symbol: 'circle',
            symbolSize: isLaptop ? 6 : 6,
          },
          {
            name: t('kpi.takeout'),
            type: 'line',
            stack: 'total',
            data: nonDineInValues,
            lineStyle: {
              width: isLaptop ? 4 : 4,
              color: '#F97F76'
            },
            itemStyle: {
              color: '#F97F76'
            },
            areaStyle: {
              color: 'rgba(249, 127, 118, 0.6)'
            },
            symbol: 'circle',
            symbolSize: isLaptop ? 6 : 6,
          }
        ]
      };
    } else {
      return {
        grid: {
          left: '4%',
          right: '4%',
          top: '14%',
          bottom: '8%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#ccc',
          borderWidth: 1,
          textStyle: {
            color: '#333',
            fontSize: isLaptop ? 11 : 11
          },
          formatter: function(params: any) {
            const monthStr = params[0].axisValue;
            let html = `<div style="padding: 3px;">
              <div style="font-weight: bold; margin-bottom: ${isLaptop ? 6 : 6}px; font-size: ${isLaptop ? 12 : 12}px;">${monthStr}</div>`;
            
            params.forEach((param: any) => {
              html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
                <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                  <span style="display: inline-block; width: ${isLaptop ? 8 : 8}px; height: ${isLaptop ? 8 : 8}px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                  <strong>${param.seriesName}:</strong>
                </span>
                <span style="font-weight: bold; font-size: ${isLaptop ? 11 : 11}px;">${tooltipFormatter(param.value)}</span>
              </div>`;
            });
            
            html += '</div>';
            return html;
          }
        },
        legend: {
          data: [t('kpi.total'), t('kpi.dineIn'), t('kpi.takeout')],
          top: '6%',
          textStyle: {
            fontSize: isiPhone ? 9 : (isLaptop ? 11 : 11),
            fontWeight: 'bold'
          },
          itemGap: isLaptop ? 15 : 15,
          selector: false,
          itemStyle: {
            borderWidth: 0
          },
          lineStyle: {
            width: isLaptop ? 3 : 3
          },
          emphasis: {
            itemStyle: {
              shadowBlur: isLaptop ? 10 : 10,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          }
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '1%',
            style: {
              text: t('kpi.chartHintSimple'),
              fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
              fontWeight: 'normal',
              fill: '#666',
              fontStyle: 'italic'
            }
          }
        ],
        xAxis: {
          type: 'category',
          data: months,
          axisLabel: {
            fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
            fontWeight: 'bold',
            rotate: months.length > 8 ? 45 : 0,
            margin: isLaptop ? 6 : 6
          },
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: yAxisLabel,
          min: yAxisRange.min,
          scale: true,
          nameTextStyle: {
            fontSize: isiPhone ? 9 : (isLaptop ? 11 : 11),
            fontWeight: 'bold'
          },
          axisLabel: {
            fontSize: isiPhone ? 8 : (isLaptop ? 10 : 10),
            formatter: (value: number) => Math.round(value).toString()
          },
          splitLine: {
            lineStyle: {
              opacity: 0.3
            }
          }
        },
        series: [
          {
            name: t('kpi.total'),
            type: 'line',
            data: totalValues,
            lineStyle: {
              width: isLaptop ? 3 : 3,
              color: '#474646'
            },
            itemStyle: {
              color: '#474646'
            },
            symbol: 'circle',
            symbolSize: isLaptop ? 6 : 6
          },
          {
            name: t('kpi.dineIn'),
            type: 'line',
            data: dineInValues,
            lineStyle: {
              width: isLaptop ? 2 : 2,
              color: '#F97F76'
            },
            itemStyle: {
              color: '#F97F76'
            },
            symbol: 'circle',
            symbolSize: isLaptop ? 5 : 5
          },
          {
            name: t('kpi.takeout'),
            type: 'line',
            data: nonDineInValues,
            lineStyle: {
              width: isLaptop ? 2 : 2,
              color: '#F9C27B'
            },
            itemStyle: {
              color: '#F9C27B'
            },
            symbol: 'circle',
            symbolSize: isLaptop ? 5 : 5
          }
        ]
      };
    }
  };

  const getComparisonOption = () => {
    if (!comparisonData || Object.keys(comparisonData).length === 0) {
      return {};
    }

    const months = [
      t('months.jan'), t('months.feb'), t('months.mar'), t('months.apr'), 
      t('months.may'), t('months.jun'), t('months.jul'), t('months.aug'), 
      t('months.sep'), t('months.oct'), t('months.nov'), t('months.dec')
    ];
    
    
    const allYears = new Set<number>();
    Object.values(comparisonData).forEach((monthData: any) => {
      monthData.forEach((yearData: any) => {
        allYears.add(yearData.year);
      });
    });
    const sortedYears = Array.from(allYears).sort();
    
    
    const yearColors = [
      '#474646', '#F97F76', '#79ADDC', '#F9C27B', '#9B59B6',
      '#1ABC9C', '#E74C3C', '#3498DB', '#F39C12', '#27AE60'
    ];

    
    const metricSeriesData = sortedYears.map((year, index) => {
      const data = months.map((month, monthIndex) => {
        const monthNumber = monthIndex + 1;
        const monthData = comparisonData[monthNumber];
        if (monthData) {
          const yearData = monthData.find((item: any) => item.year === year);
          if (yearData) {
            
            switch (metricKey) {
              case 'net_sales':
                
                const daysInMonth = new Date(year, monthNumber, 0).getDate();
                return yearData.net_sales / daysInMonth;
              case 'order_count':
                const daysInMonth2 = new Date(year, monthNumber, 0).getDate();
                return yearData.order_count / daysInMonth2;
              case 'avg_ticket':
                
                return (yearData.dine_in_avg_ticket + yearData.non_dine_in_avg_ticket) / 2 || 0;
              case 'avg_processing_time':
                
                return (yearData.dine_in_avg_processing_time + yearData.non_dine_in_avg_processing_time) / 2 || 0;
              default:
                return 0;
            }
          }
        }
        return 0;
      });

      return {
        name: t('kpi.year', { year }),
        type: 'bar',
        data: data,
        itemStyle: {
          color: yearColors[index % yearColors.length]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.3)'
          }
        },
        barMaxWidth: isLaptop ? 25 : 30
      };
    });

    
    let yAxisFormatter = (value: number) => value.toString();
    let tooltipFormatter = (value: number) => value.toString();
    let yAxisName = '';

    switch (metricKey) {
      case 'net_sales':
        yAxisFormatter = (value: number) => `$${Math.round(value)}`;
        tooltipFormatter = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        yAxisName = t('kpi.yAxisDailyAvgSales');
        break;
      case 'order_count':
        yAxisFormatter = (value: number) => Math.round(value).toString();
        tooltipFormatter = (value: number) => Math.round(value).toLocaleString();
        yAxisName = t('kpi.yAxisDailyOrders');
        break;
      case 'avg_ticket':
        yAxisFormatter = (value: number) => `$${Math.round(value)}`;
        tooltipFormatter = (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        yAxisName = t('kpi.yAxisAvgTicket');
        break;
      case 'avg_processing_time':
        yAxisFormatter = (value: number) => `${Math.round(value)}s`;
        tooltipFormatter = (value: number) => `${Math.round(value)}${t('kpi.secondsSuffix')}`;
        yAxisName = t('kpi.yAxisAvgTime');
        break;
    }

    const legendData = metricSeriesData.map(series => series.name);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 12 : 13
        },
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${t('kpi.comparisonTooltip', { month: monthStr })}</div>`;
          
          params.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 5px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold;">${tooltipFormatter(param.value)}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: legendData,
        top: isLaptop ? 15 : 20,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 11 : 12,
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 15 : 20
      },
      grid: {
        left: '4%',
        right: '4%',
        bottom: '8%',
        top: isLaptop ? '80px' : '90px',
        containLabel: true
      },
      xAxis: [{
        type: 'category',
        data: months,
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          interval: 0,
          fontSize: isLaptop ? 10 : 11,
          color: '#333',
          fontWeight: 'bold'
        },
        axisLine: {
          lineStyle: {
            width: isLaptop ? 1.5 : 2
          }
        }
      }],
      yAxis: [{
        type: 'value',
        name: yAxisName,
        min: 0,
        axisLabel: {
          formatter: yAxisFormatter,
          color: '#333',
          fontSize: isLaptop ? 10 : 11,
          fontWeight: 'bold'
        },
        nameTextStyle: {
          color: '#333',
          fontSize: isLaptop ? 11 : 12,
          fontWeight: 'bold'
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#474646',
            width: isLaptop ? 1.5 : 2
          }
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      }],
      series: metricSeriesData
    };
  };

  
  if (isLoading || isFiltersApplying) {
    return (
      <div className="kpi-card kpi-card-loading" style={{'--primary-color': color, '--accent-color': accentColor} as React.CSSProperties}>
        <div className="kpi-card-header">
          {icon && <span className="kpi-card-icon">{icon}</span>}
          <h3 className="kpi-card-title">{title}</h3>
        </div>
        <div className="kpi-card-content">
          <div className="kpi-card-loader"></div>
          {isFiltersApplying && (
            <div className="kpi-updating-text" style={{ fontSize: isLaptop ? '14px' : '14px' }}>
              {t('kpi.filterApplying')}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  if (!totalData) {
    return (
      <div className="kpi-card" style={{'--primary-color': color, '--accent-color': accentColor} as React.CSSProperties}>
        <div className="kpi-card-header">
          {icon && <span className="kpi-card-icon">{icon}</span>}
          <h3 className="kpi-card-title">{title}</h3>
        </div>
        <div className="kpi-card-content">
          <p className="kpi-card-no-data" style={{ fontSize: isLaptop ? '14px' : '14px' }}>
            {t('kpi.noData')}
          </p>
        </div>
      </div>
    );
  }
  
  const totalValue = totalData[metricKey];
  const dineInValue = dineInData?.[metricKey];
  const nonDineInValue = nonDineInData?.[metricKey];
  
  
  const dineInNumValue = dineInValue ? 
    (typeof dineInValue === 'string' ? 
      parseFloat(dineInValue.replace(/[$,]/g, '')) : 
      Number(dineInValue)) : 
    0;
  
  const nonDineInNumValue = nonDineInValue ? 
    (typeof nonDineInValue === 'string' ? 
      parseFloat(nonDineInValue.replace(/[$,]/g, '')) : 
      Number(nonDineInValue)) : 
    0;
  
  const higherValue = Math.max(dineInNumValue, nonDineInNumValue);
  const lowerValue = Math.min(dineInNumValue, nonDineInNumValue);
  const difference = higherValue - lowerValue;
  const percentDifference = lowerValue > 0 ? ((difference / lowerValue) * 100) : 0;
  
  const isDineInHigher = dineInNumValue > nonDineInNumValue;
  const isEqual = dineInNumValue === nonDineInNumValue;
  
  const totalSum = dineInNumValue + nonDineInNumValue;
  const dineInPercentage = totalSum > 0 ? (dineInNumValue / totalSum) * 100 : 0;
  const nonDineInPercentage = totalSum > 0 ? (nonDineInNumValue / totalSum) * 100 : 0;

  return (
    <>
      <div className="kpi-card" style={{'--primary-color': color, '--accent-color': accentColor} as React.CSSProperties}>
        {isFiltersApplying && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span style={{ fontSize: isLaptop ? '14px' : '14px' }}>
              {t('kpi.updating')}
            </span>
          </div>
        )}
        
        <div className="kpi-card-header">
          <div className="kpi-card-header-left">
            {icon && <span className="kpi-card-icon">{icon}</span>}
            <h3 className="kpi-card-title" style={{ fontSize: isLaptop ? '16px' : '16px' }}>
              {title}
            </h3>
          </div>
          <div className="kpi-card-header-right">
            <button 
              className="kpi-trend-button" 
              onClick={handleTrendButtonClick}
              disabled={trendLoading || isFiltersApplying}
              title={t('kpi.viewMonthlyTrend')}
              style={{ marginRight: '2px' }}
            >
              {trendLoading && viewMode === 'monthly' ? '⟳' : '📈'}  
            </button>
            <button 
              className="kpi-trend-button" 
              onClick={handleComparisonButtonClick}
              disabled={trendLoading || isFiltersApplying}
              title={t('kpi.viewMonthComparison')}
            >
              {trendLoading && viewMode === 'comparison' ? '⟳' : '📊'}  
            </button>
          </div>
        </div>
        
        <div className="kpi-card-content">
          <div className="kpi-total-value">
            <div className="kpi-value-amount" style={{ fontSize: isLaptop ? '24px' : '24px' }}>
              {formatFn(totalValue)}
            </div>
            <div className="kpi-value-label" style={{ fontSize: isLaptop ? '14px' : '14px' }}>
              {t('kpi.total')}
            </div>
          </div>
          
          <div className="kpi-comparison">
            <div className="kpi-comparison-items">
              <div className={`kpi-comparison-item ${isDineInHigher && !isEqual ? 'higher' : 'lower'}`}>
                <div className="kpi-comparison-label">
                  <div className="kpi-comparison-color dine-in"></div>
                  <span style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {t('kpi.dineIn')}
                  </span>
                  {isDineInHigher && !isEqual && <span className="comparison-arrow">↑</span>}
                  {!isDineInHigher && !isEqual && <span className="comparison-arrow down">↓</span>}
                </div>
                <div className="kpi-comparison-right">
                  <div className="kpi-comparison-value" style={{ fontSize: isLaptop ? '16px' : '16px' }}>
                    {dineInValue ? formatFn(dineInValue) : '0'}
                  </div>
                  {showPercentage && (
                    <div className="kpi-percentage" style={{ fontSize: isLaptop ? '12px' : '12px' }}>
                      {dineInPercentage.toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
              
              <div className={`kpi-comparison-item ${!isDineInHigher && !isEqual ? 'higher' : 'lower'}`}>
                <div className="kpi-comparison-label">
                  <div className="kpi-comparison-color non-dine-in"></div>
                  <span style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {t('kpi.takeout')}
                  </span>
                  {!isDineInHigher && !isEqual && <span className="comparison-arrow">↑</span>}
                  {isDineInHigher && !isEqual && <span className="comparison-arrow down">↓</span>}
                </div>
                <div className="kpi-comparison-right">
                  <div className="kpi-comparison-value" style={{ fontSize: isLaptop ? '16px' : '16px' }}>
                    {nonDineInValue ? formatFn(nonDineInValue) : '0'}
                  </div>
                  {showPercentage && (
                    <div className="kpi-percentage" style={{ fontSize: isLaptop ? '12px' : '12px' }}>
                      {nonDineInPercentage.toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {!isEqual && (
              <div className="kpi-difference-info">
                <div className="difference-text">
                  <span className="difference-amount" style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {formatFn(difference)}
                  </span>
                  <span className="difference-percent" style={{ fontSize: isLaptop ? '12px' : '12px' }}>
                    ({percentDifference.toFixed(1)}% {t('kpi.difference')})
                  </span>
                </div>
                <div className="difference-label" style={{ fontSize: isLaptop ? '12px' : '12px' }}>
                  {isDineInHigher ? t('kpi.dineInHigher') : t('kpi.takeoutHigher')}
                </div>
              </div>
            )}
            
            {isEqual && (
              <div className="kpi-difference-info">
                <div className="difference-text equal" style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                  {t('kpi.equal')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="kpi-trend-overlay" onClick={closeModal}>
          <div 
            className="kpi-trend-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: '50%',
              top: `${modalPosition.y}px`,
              transform: 'translateX(-50%)',
              width: isLaptop ? '85%' : '85%',
              maxWidth: isLaptop ? '900px' : '900px'
            }}
          >
            <div className="kpi-trend-modal-header">
              <h3 className="kpi-trend-modal-title" style={{ fontSize: isiPhone ? '12px' : (isLaptop ? '18px' : '18px') }}>
                {viewMode === 'comparison' 
                  ? t('kpi.modalComparisonTitle', { title })
                  : t('kpi.modalTitle', { title })
                }
              </h3>
              <div className="kpi-trend-modal-subtitle" style={{ fontSize: isiPhone ? '10px' : (isLaptop ? '14px' : '14px') }}>
                {viewMode === 'comparison'
                  ? t('kpi.modalComparisonSubtitle')
                  : t('kpi.modalSubtitle')
                }
              </div>
              <button className="kpi-trend-modal-close" onClick={closeModal} style={{ fontSize: isLaptop ? '18px' : '18px' }}>
                ✕
              </button>
            </div>
            <div className="kpi-trend-modal-content" style={{ height: isLaptop ? '500px' : '500px' }}>
              {trendLoading ? (
                <div className="kpi-trend-loading">
                  <div className="kpi-trend-loader"></div>
                  <div style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {viewMode === 'comparison' 
                      ? t('kpi.modalComparisonLoading')
                      : t('kpi.modalLoading')
                    }
                  </div>
                </div>
              ) : trendError ? (
                <div className="kpi-trend-error">
                  <div className="kpi-trend-error-icon">⚠️</div>
                  <div style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {trendErrorMessage}
                  </div>
                </div>
              ) : (viewMode === 'monthly' && monthlyData.length > 0) || 
                  (viewMode === 'comparison' && comparisonData && Object.keys(comparisonData).length > 0) ? (
                <ReactECharts 
                  ref={chartRef}
                  option={getTrendOption()} 
                  style={{height: '100%', width: '100%'}}
                  className="kpi-trend-chart"
                  opts={{renderer: 'canvas'}}
                  onEvents={(metricKey === 'order_count' || metricKey === 'net_sales') ? getChartEvents() : undefined}
                />
              ) : (
                <div className="kpi-trend-empty">
                  <div style={{ fontSize: isLaptop ? '14px' : '14px' }}>
                    {viewMode === 'comparison' 
                      ? t('kpi.modalComparisonEmpty')
                      : t('kpi.modalEmpty')
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KpiCard;